import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { MAX_UPLOAD_LABEL, READING_TIME_LABEL } from '../lib/constants'
import type { PublicQueueItem, PublicStats } from '../types'
import { Loading } from '../components/Loading'

const EMPTY_STATS: PublicStats = {
  queued_count: 0,
  in_progress_count: 0,
  completed_total: 0,
  completed_last_7_days: 0,
  completed_last_30_days: 0,
  average_per_day_30: 0,
  estimated_wait_days: null,
  average_wait_days_30: null,
}

export function HomePage() {
  const [queue, setQueue] = useState<PublicQueueItem[]>([])
  const [stats, setStats] = useState<PublicStats>(EMPTY_STATS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const [queueResult, statsResult] = await Promise.all([
        supabase.rpc('get_public_queue'),
        supabase.rpc('get_public_stats'),
      ])

      if (queueResult.error || statsResult.error) {
        console.error(queueResult.error || statsResult.error)
        setError('Public queue data is temporarily unavailable.')
      } else {
        setQueue((queueResult.data ?? []) as PublicQueueItem[])
        const first = Array.isArray(statsResult.data) ? statsResult.data[0] : statsResult.data
        setStats((first ?? EMPTY_STATS) as PublicStats)
      }
      setLoading(false)
    }

    void load()
    const timer = window.setInterval(() => void load(), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <>
      <section className="hero">
        <div className="container hero-grid">
          <div>
            <h1>A rapid first look at your scientific work.</h1>
            <p className="hero-copy">
              I will spend {READING_TIME_LABEL} with your manuscript and send you private
              feedback on whatever stands out: the idea, method, abstract, structure,
              figures, layout, formatting, or overall reception.
            </p>
            <div className="hero-actions">
              <div className="primary-action-group">
                <Link className="button button-primary" to="/auth">
                  Submit a manuscript
                </Link>
                <small>Private PDF up to {MAX_UPLOAD_LABEL} or public arXiv identifier</small>
              </div>
              <a className="button button-secondary" href="#current-queue">
                Current queue
              </a>
              <a className="button button-secondary" href="#how-it-works">
                How it works
              </a>
            </div>
          </div>

          <div className="hero-panel" aria-label="Service summary">
            <div className="feature-title">What you can expect</div>
            <div className="divider" />
            <ul className="check-list">
              <li>First look by an academic researcher</li>
              <li>Every eligible submission receives feedback</li>
              <li>Private feedback, visible only to you</li>
              <li>Private uploads are never published or shared</li>
              <li>arXiv manuscripts are read directly from arXiv</li>
              <li>No AI analysis</li>
              <li>No payment, authorship, or acknowledgment requested</li>
              <li>Uploaded source PDFs are deleted after feedback</li>
            </ul>
          </div>
        </div>
      </section>

      <section
        className="section section-soft"
        id="current-queue"
        aria-label="Current queue"
      >
        <div className="container">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow">Current queue</p>
            </div>
            <p>First submitted, first examined.</p>
          </div>

          {loading ? (
            <Loading label="Loading public queue..." />
          ) : error ? (
            <div className="notice notice-error">{error}</div>
          ) : (
            <>
              <div className="stats-grid">
                <article className="stat-card">
                  <strong>{stats.queued_count}</strong>
                  <span>manuscripts waiting</span>
                </article>
                <article className="stat-card">
                  <strong>{stats.completed_last_7_days}</strong>
                  <span>feedbacks in the last 7 days</span>
                </article>
                <article className="stat-card">
                  <strong>{stats.average_per_day_30.toFixed(2)}</strong>
                  <span>average per day, last 30 days</span>
                </article>
                <article className="stat-card">
                  <strong>
                    {stats.estimated_wait_days === null
                      ? 'Not enough data'
                      : `${stats.estimated_wait_days} days`}
                  </strong>
                  <span>estimated wait for feedback</span>
                </article>
              </div>

              <div className="queue-card">
                <div className="queue-header">
                  <h3>Anonymous public queue</h3>
                  <span>{queue.length} active item(s)</span>
                </div>
                {queue.length === 0 ? (
                  <p className="empty-state">The queue is currently empty.</p>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Position</th>
                          <th>Anonymous code</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {queue.map((item) => (
                          <tr key={item.public_code}>
                            <td>{item.position}</td>
                            <td className="mono">{item.public_code}</td>
                            <td>{item.status === 'in_progress' ? 'Being read' : 'In queue'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="section" id="how-it-works">
        <div className="container">
          <div className="section-heading">
            <p className="eyebrow">How it works</p>
          </div>

          <div className="steps-grid">
            <article className="step-card">
              <span>01</span>
              <h3>Create an account</h3>
              <p>Create an account using your email address and password.</p>
            </article>

            <article className="step-card">
              <span>02</span>
              <h3>Submit one manuscript</h3>
              <p>
                Upload a private PDF up to {MAX_UPLOAD_LABEL}, or provide the identifier of
                your publicly available arXiv manuscript. You must be an author or coauthor.
              </p>
            </article>

            <article className="step-card">
              <span>03</span>
              <h3>Follow the queue</h3>
              <p>
                Your public code is shown anonymously. The service follows submission
                order, without paid or urgent priority.
              </p>
            </article>

            <article className="step-card">
              <span>04</span>
              <h3>Receive scientific feedback</h3>
              <p>
                I will provide private feedback on your work from the perspective of a
                researcher encountering it for the first time.
              </p>
            </article>

            <article className="step-card">
              <span>05</span>
              <h3>Private PDFs are deleted</h3>
              <p>
                After feedback is published, an uploaded source PDF is deleted from active
                storage. For an arXiv submission, no manuscript copy is stored by the service.
              </p>
            </article>
          </div>

          <div className="process-comment">
            <p>
              The feedback is intended as support: a way to understand how your work may
              be received by a researcher who did not develop it and sees it for the first
              time under a strict time limit.
            </p>
            <p>
              The comments may be positive, critical, technical, editorial, visual, or
              very high-level. Their depth depends on the topic and its proximity to my
              expertise. There are no scores, no acceptance prediction, and no rebuttal.
            </p>
            <p>
              A manuscript may not receive substantive feedback if it is unsafe or technically
              inaccessible, if its submission is unauthorized, or if it substantially overlaps
              with my current research.
            </p>
          </div>
        </div>
      </section>
    </>
  )
}
